// API Service for La Vie en Rose
const API_URL = process.env.REACT_APP_BACKEND_URL;

// Resolve relative asset URLs (like /api/uploads/...) to absolute URLs
export function resolveAssetUrl(url) {
  if (!url) return '';
  if (/^https?:\/\//i.test(url) || url.startsWith('data:')) return url;
  if (url.startsWith('/')) return `${API_URL}${url}`;
  return url;
}

// Get stored admin token
function getAdminToken() {
  return sessionStorage.getItem('admin_token');
}

// Helper function for API calls
async function apiCall(endpoint, options = {}) {
  const url = `${API_URL}/api${endpoint}`;
  const { headers: optHeaders, ...restOptions } = options;
  const headers = {
    'Content-Type': 'application/json',
    ...optHeaders,
  };
  const token = getAdminToken();
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
  }
  const response = await fetch(url, {
    ...restOptions,
    headers,
  });
  
  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    const detail = Array.isArray(err.detail)
      ? err.detail.map((d) => (d.msg || '').replace(/^Value error, /, '')).join('. ')
      : err.detail;
    const error = new Error(detail || `API Error: ${response.status}`);
    error.status = response.status;
    throw error;
  }
  
  return response.json();
}

// Admin Auth
export const adminLogin = async (email, password) => {
  const res = await apiCall('/admin/login', {
    method: 'POST',
    body: JSON.stringify({ email, password }),
  });
  sessionStorage.setItem('admin_token', res.token);
  return res;
};
export const adminMe = () => apiCall('/admin/me');
export const adminLogout = () => {
  sessionStorage.removeItem('admin_token');
};

// Restaurant Info
export const getRestaurantInfo = () => apiCall('/restaurant');
export const updateRestaurantInfo = (data) => apiCall('/restaurant', {
  method: 'PUT',
  body: JSON.stringify(data),
});

// Menu
export const getMenu = () => apiCall('/menu');
export const getMenuCategory = (categoryId) => apiCall(`/menu/${categoryId}`);
export const updateMenuCategory = (categoryId, data) => apiCall(`/menu/${categoryId}`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const createMenuCategory = (data) => apiCall('/menu', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const patchMenuCategory = (categoryId, data) => apiCall(`/menu/${categoryId}`, {
  method: 'PATCH',
  body: JSON.stringify(data),
});
export const deleteMenuCategory = (categoryId) => apiCall(`/menu/${categoryId}`, {
  method: 'DELETE',
});
export const addMenuItem = (categoryId, data) => apiCall(`/menu/${categoryId}/items`, {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateMenuItem = (categoryId, itemId, data) => apiCall(`/menu/${categoryId}/items/${itemId}`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteMenuItem = (categoryId, itemId) => apiCall(`/menu/${categoryId}/items/${itemId}`, {
  method: 'DELETE',
});

// Chef's Specials
export const getChefSpecials = () => apiCall('/specials');
export const createChefSpecial = (data) => apiCall('/specials', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateChefSpecial = (id, data) => apiCall(`/specials/${id}`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteChefSpecial = (id) => apiCall(`/specials/${id}`, {
  method: 'DELETE',
});

// Chef Info
export const getChefInfo = () => apiCall('/chef');
export const updateChefInfo = (data) => apiCall('/chef', {
  method: 'PUT',
  body: JSON.stringify(data),
});

// Testimonials
export const getTestimonials = () => apiCall('/testimonials');
export const createTestimonial = (data) => apiCall('/testimonials', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const deleteTestimonial = (id) => apiCall(`/testimonials/${id}`, {
  method: 'DELETE',
});

// Gallery
export const getGallery = () => apiCall('/gallery');
export const addGalleryImage = (data) => apiCall('/gallery', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const updateGalleryImage = (id, data) => apiCall(`/gallery/${id}`, {
  method: 'PUT',
  body: JSON.stringify(data),
});
export const deleteGalleryImage = (id) => apiCall(`/gallery/${id}`, {
  method: 'DELETE',
});
export const uploadGalleryImage = async (file, alt = '') => {
  const formData = new FormData();
  formData.append('file', file);
  formData.append('alt', alt);
  const token = getAdminToken();
  const headers = {};
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(`${API_URL}/api/gallery/upload`, {
    method: 'POST',
    headers,
    body: formData,
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Upload failed: ${res.status}`);
  }
  return res.json();
};

// Reservations
export const getReservations = (sort = 'recent') => apiCall(`/reservations?sort=${encodeURIComponent(sort)}`);
export const createReservation = (data) => apiCall('/reservations', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const dismissReservationNotification = (id) => apiCall(`/reservations/${id}/dismiss`, {
  method: 'POST',
});
export const updateReservationStatus = (id, status, options = {}) => {
  const body = { status };
  // Backward-compat: allow passing a string as cancellation_reason for legacy calls
  if (typeof options === 'string') {
    body.cancellation_reason = options;
  } else if (options && typeof options === 'object') {
    if (options.cancellation_reason !== undefined && options.cancellation_reason !== null) {
      body.cancellation_reason = options.cancellation_reason;
    }
    if (options.confirmation_message !== undefined && options.confirmation_message !== null) {
      body.confirmation_message = options.confirmation_message;
    }
  }
  return apiCall(`/reservations/${id}`, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
};
export const deleteReservation = (id) => apiCall(`/reservations/${id}`, {
  method: 'DELETE',
});

// Blocked dates/times
export const getBlockedSlots = () => apiCall('/blocked-slots');
export const createBlockedSlot = (data) => apiCall('/blocked-slots', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const deleteBlockedSlot = (id) => apiCall(`/blocked-slots/${id}`, {
  method: 'DELETE',
});

// Contact Messages
export const getContactMessages = () => apiCall('/contacts');
export const createContactMessage = (data) => apiCall('/contacts', {
  method: 'POST',
  body: JSON.stringify(data),
});
export const markMessageAsRead = (id) => apiCall(`/contacts/${id}/read`, {
  method: 'PUT',
});
export const deleteContactMessage = (id) => apiCall(`/contacts/${id}`, {
  method: 'DELETE',
});

// Initialize data
export const initializeData = () => apiCall('/init-data', { method: 'POST' });
